/**
 * @file: useSystemModuleStats.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { SystemModuleStats } from '@euler/app/tv/components/inspection/BottomTabs';
import { getSiteInspectionDefectiveLevelPatched } from '@euler/app/tv/functions/getSiteInspectionDefectiveLevelPatched';
import { getDefectiveLevel } from '@euler/functions/getDefectiveLevel';
import { DefectiveLevel, makeDefectiveSummary } from '@euler/model';
import { useMemo } from 'react';

export const useSystemModuleStats = () => {
  const { detail, inventoryQuery } = useTaskContext();

  const stats = useMemo<SystemModuleStats>(() => {
    const res: SystemModuleStats = new Map();
    const update = (modId: number, defectiveLevel: DefectiveLevel) => {
      let info = res.get(modId);
      if (!info) {
        info = {
          defectiveStats: makeDefectiveSummary(),
          defectiveLevel,
        };
        res.set(modId, info);
      }
      info.defectiveStats[defectiveLevel]++;
      if (defectiveLevel > info.defectiveLevel) {
        info.defectiveLevel = defectiveLevel;
      }
    };
    for (const inspectedSite of detail.inspection.sites) {
      const siteId = inspectedSite.siteId;
      const site = inventoryQuery.getSiteById(siteId);
      const modId = site?.primarySysModId ?? 0;
      const defectiveLevel =
        getSiteInspectionDefectiveLevelPatched(inspectedSite);
      update(modId, defectiveLevel);
    }
    if (detail.inspection.customIssues != null) {
      for (const customIssue of detail.inspection.customIssues) {
        const siteId = customIssue.siteId ?? 0;
        const site = inventoryQuery.getSiteById(siteId);
        const modId = site?.primarySysModId ?? 0;
        const defectiveLevel = getDefectiveLevel(
          customIssue.abnormalLevel,
          customIssue.severityLevel,
        );
        update(modId, defectiveLevel);
      }
    }
    return res;
  }, [detail.inspection.customIssues, detail.inspection.sites, inventoryQuery]);

  return stats;
};
